import { isValidElement, ReactNode, useEffect, useMemo, useState } from "react";
import { CustomerResponse } from "../../../types/customer.type";
import { customerStatusDisplay } from "../data";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PrintIcon from "@mui/icons-material/Print";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import DiscountModal from "./DiscountModal";
import PriceBookModal from "./PriceBookModal";
import { serviceModeLabel } from "../../SpecialOrder/data";
import { useSpecialOrderBlankForm } from "../../SpecialOrder/useSpecialOrderDocuments";

const compact = "@media " + COMPACT_QUERY;

interface CustomerInfoProps {
  customerDetails: CustomerResponse;
  /** Re-fetches the customer so a saved discount shows without a page reload. */
  onUpdated?: () => void;
}

interface DetailsProps {
  label: string;
  value: string | number | ReactNode;
}

const GrayText = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="caption"
    sx={{ color: (theme) => theme.palette.gray.main }}
  >
    {children}
  </Typography>
);

const Details = ({ label, value }: DetailsProps) => (
  <>
    <Grid size={4}>
      <Typography variant="caption" fontWeight={600}>
        {label}
      </Typography>
    </Grid>
    <Grid size={6}>
      {isValidElement(value) ? value : <GrayText>{value}</GrayText>}
    </Grid>
    <Grid size={2} />
  </>
);

/** KTB buyer types read back for display; the raw code means nothing to a person. */
const BUYER_TYPE_LABEL: Record<string, string> = {
  TXID: "นิติบุคคล",
  NIDN: "บุคคลธรรมดา",
  CCPT: "หนังสือเดินทาง",
  OTHR: "อื่น ๆ",
};

export default function CustomerInfo({
  customerDetails,
  onUpdated,
}: CustomerInfoProps) {
  const role = useUserRole();
  const canEditDiscount = featureAccess.customerDiscount(role);
  const canBindPricebook = featureAccess.customerPricebookBind(role);
  // The hand-written order form belongs to ออเดอร์พิเศษ, so it follows that menu's gate.
  const canPrintBlankForm = featureAccess.specialOrder(role);
  const blankForm = useSpecialOrderBlankForm(customerDetails.uuid);
  const [discountOpen, setDiscountOpen] = useState(false);
  const [priceBookOpen, setPriceBookOpen] = useState(false);

  const handleOpenPriceBook = () => setPriceBookOpen(true);
  const handleClosePriceBook = () => setPriceBookOpen(false);
  const handleSaved = () => onUpdated?.();

  const image = useMemo(() => {
    return (
      customerDetails.thumbnail ??
      customerDetails.picture ??
      customerDetails.picture_1 ??
      customerDetails.picture_2 ??
      customerDetails.picture_3
    );
  }, [
    customerDetails.picture,
    customerDetails.picture_1,
    customerDetails.picture_2,
    customerDetails.picture_3,
    customerDetails.thumbnail,
  ]);

  // A stored URL can still fail to load (file removed from the bucket, expired link), which
  // otherwise leaves a broken-image box. Every fetch hands back a fresh payload, so the load
  // is retried on each one: a refetch can return the same URL, and one dead load must not
  // hide the photo for the life of the page.
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  useEffect(() => {
    setImageLoadFailed(false);
  }, [customerDetails]);

  const handleImageError = () => setImageLoadFailed(true);

  const imageSource = imageLoadFailed ? undefined : image;

  return (
    <Paper sx={{ padding: "16px 24px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box>
          <GrayText>รหัสลูกค้า {customerDetails.customer_code}</GrayText>
          <Typography variant="h6" fontWeight={700}>
            {customerDetails.name}
          </Typography>
        </Box>
        {canPrintBlankForm && (
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            loading={blankForm.busy}
            onClick={blankForm.print}
            sx={{ flexShrink: 0 }}
          >
            ดาวน์โหลดฟอร์มกรอกมือ (A4)
          </Button>
        )}
      </Box>

      <Grid container sx={{ mt: 2 }}>
        <Grid size={3}>
          <Box
            sx={{
              width: 280,
              height: 280,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: (theme) =>
                `1px solid ${theme.palette.background.default}`,
              [compact]: {
                width: "100%",
                maxWidth: 200,
                height: 200,
              },
            }}
          >
            {imageSource ? (
              <Box
                component="img"
                alt=""
                src={imageSource}
                onError={handleImageError}
                sx={{
                  height: 250,
                  width: 250,
                  [compact]: { height: "100%", width: "100%", objectFit: "contain" },
                }}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  textAlign: "center",
                }}
              >
                ไม่มีรูปภาพ
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid size={5}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            ข้อมูลทั่วไป
          </Typography>
          <Grid container>
            <Details label="รหัสลูกค้า" value={customerDetails.customer_code} />
            <Details label="ชื่อลูกค้า" value={customerDetails.name} />
            <Details label="หมวดลูกค้า" value={"-"} />
            <Details
              label="ช่องทางการขาย"
              value={customerDetails.channel ?? "-"}
            />
            <Details
              label="เปิดใช้งานสูตร"
              value={
                <Box sx={{ display: "flex", alignItems: "center", mt: "4px" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: (theme) =>
                        theme.palette[
                          customerStatusDisplay(customerDetails.status).color
                        ].main,
                      mr: 1,
                    }}
                  />
                  <GrayText>
                    {customerStatusDisplay(customerDetails.status).label}
                  </GrayText>
                </Box>
              }
            />
            <Details
              label="ราคาสินค้ารวม Vat"
              value={
                <Box sx={{ display: "flex", alignItems: "center", mt: "4px" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: (theme) =>
                        customerDetails.taxtype === "IN"
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                      mr: 1,
                    }}
                  />
                  <GrayText>{customerDetails.taxtype}</GrayText>
                </Box>
              }
            />
            <Details label="รหัสผู้ขาย" value={"-"} />
            <Details
              label="ประเภทบิล"
              value={
                <Chip
                  label={customerDetails.billtype}
                  color={
                    customerDetails.billtype === "cash" ? "primary" : "success"
                  }
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                />
              }
            />
            <Details
              label="แบบบริการออเดอร์พิเศษ"
              value={serviceModeLabel(customerDetails.service_mode)}
            />
          </Grid>
        </Grid>

        <Grid size={4}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            ข้อมูลเพิ่มเติม
          </Typography>
          <Grid container>
            <Details
              label="เลขอ้างอิง 1"
              value={customerDetails.reference1 ?? "-"}
            />
            <Details
              label="เลขอ้างอิง 2"
              value={customerDetails.reference2 ?? "-"}
            />
            {/* The stored code. The four rows under it are derived from it by the API
                on write, so this is the one that may be edited. */}
            <Details
              label="สมุดราคา"
              value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <GrayText>{customerDetails.pricebook ?? "-"}</GrayText>
                  {canBindPricebook && (
                    <IconButton
                      size="small"
                      aria-label="แก้ไขสมุดราคา"
                      onClick={handleOpenPriceBook}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            />
            <Details
              label="สมุดราคาขายเงินสด"
              value={customerDetails.pricebook_ca ?? "-"}
            />
            <Details
              label="สมุดราคาใบสั่งขาย"
              value={customerDetails.pricebook_so ?? "-"}
            />
            <Details
              label="สมุดราคาใบแจ้งหนี้"
              value={customerDetails.pricebook_iv ?? "-"}
            />
            <Details
              label="สมุดราคาใบเสนอราคา"
              value={customerDetails.pricebook_qo ?? "-"}
            />
            <Details
              label="ส่วนลดท้ายบิล"
              value={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {customerDetails.discount_percent != null ? (
                    <Chip
                      label={`ลด ${customerDetails.discount_percent}%`}
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  ) : (
                    <GrayText>ไม่มี</GrayText>
                  )}
                  {canEditDiscount && (
                    <IconButton
                      size="small"
                      aria-label="แก้ไขส่วนลดท้ายบิล"
                      onClick={() => setDiscountOpen(true)}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            />
            <Details label="ที่อยู่" value={"-"} />
          </Grid>
        </Grid>

        <Grid size={3} />

        <Grid size={5}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            ใบกำกับภาษี
          </Typography>
          <Grid container>
            {/* First, because it decides whether anything below is ever sent anywhere. A shop
                on paper is not a shop with missing data — it is a shop that did not ask. */}
            <Details
              label="ช่องทาง"
              value={customerDetails.etax_enabled ? "eTax" : "กระดาษ"}
            />
            <Details
              label="ประเภทผู้ซื้อ"
              value={
                customerDetails.inv_taxid_type
                  ? BUYER_TYPE_LABEL[customerDetails.inv_taxid_type] ??
                    customerDetails.inv_taxid_type
                  : null
              }
            />
            <Details label="ชื่อบริษัท" value={customerDetails.inv_name} />
            <Details label="เลขผู้เสียภาษี" value={customerDetails.inv_taxid} />
            <Details label="รหัสสาขา" value={customerDetails.inv_branchcode} />
            <Details label="ชื่อสาขา" value={customerDetails.inv_branchname} />
            <Details label="ที่อยู่" value={customerDetails.inv_address} />
          </Grid>
        </Grid>

        <Grid size={4}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            ข้อมูลเครดิต
          </Typography>
          <Grid container>
            <Details label="วงเงินเครดิต" value={customerDetails.creditlimit} />
            <Details
              label="วงเงินเครดิตที่ใช้ได้"
              value={customerDetails.credit_available}
            />
            <Details
              label="ระยะเวลาเครดิต"
              value={customerDetails.creditterm}
            />
            <Details
              label="ประเภทบิล"
              value={
                <Chip
                  label={customerDetails.bill_type2}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "capitalize" }}
                />
              }
            />
          </Grid>
        </Grid>
      </Grid>

      {canBindPricebook && (
        <PriceBookModal
          open={priceBookOpen}
          customerUuid={customerDetails.uuid}
          customerName={customerDetails.name ?? ""}
          customerCode={customerDetails.customer_code ?? ""}
          priceBookCode={customerDetails.pricebook ?? null}
          branchCompanyId={customerDetails.company_id ?? null}
          onClose={handleClosePriceBook}
          onSaved={handleSaved}
        />
      )}

      {canEditDiscount && (
        <DiscountModal
          open={discountOpen}
          customerUuid={customerDetails.uuid}
          customerName={customerDetails.name ?? ""}
          customerCode={customerDetails.customer_code ?? ""}
          discountPercent={customerDetails.discount_percent ?? null}
          onClose={() => setDiscountOpen(false)}
          onSaved={() => onUpdated?.()}
        />
      )}
    </Paper>
  );
}
